/**
 * Lookup-enum eligibility resolver.
 *
 * A column can back a **lookup enum** (values fetched from the referenced
 * table, keys stored, labels rendered) only when it resolves to *exactly one
 * unconditional FK target*:
 *
 *   - a real FK constraint (always wins — it's the strongest signal), or
 *   - exactly one loose relationship with no `sourceFilter` and no
 *     `sourcePath`.
 *
 * Everything else is ineligible in V1 and the caller (declare dialog) explains
 * why using the returned `reason`:
 *   - `'array'`       — array-typed column (checked first, even over a real FK;
 *                       mirrors `resolveCellEditVariant`'s array short-circuit).
 *   - `'polymorphic'` — several candidate targets on the same column, or a
 *                       source-filtered (polymorphic-dispatch) declaration.
 *   - `'json-path'`   — every declaration points at a JSON sub-key.
 *   - `'none'`        — nothing to look up.
 *
 * Pure — no React, no hooks, no I/O. Callers pass the merged FK map's real-FK
 * entry and the loose rels already narrowed to the source
 * `(fromSchema, fromTable, fromColumn)` triple (`findLooseRelationshipsForColumn`).
 *
 * Kusto invariant: schema values may legitimately be `''`. Never truthiness-
 * check a schema here — `''` flows through as a valid schema.
 */

import type { EnumLookupTarget, LooseRelationship } from '../../../../types/electron';
import type { FilterGroup } from '../../core/types/workspaceTypes';

/**
 * Structural shape of a *real* FK entry (matches `ColumnForeignKeyInfo` from
 * `useTableMetadata` without importing the hook module into this pure util).
 * `referencedSchema` is absent on engines that don't carry one.
 *
 * Callers must pass only real-constraint entries — a merged-map entry with
 * `isLoose: true` belongs in `looseRels`, not here.
 */
export type LookupEnumRealFkInput = {
  referencedSchema?: string;
  referencedTable: string;
  referencedColumn: string;
};

export type LookupEnumTargetResolution =
  | {
      target: EnumLookupTarget;
      /** The winning loose FK's target filter — for the browse-all-rows escape. Absent for real FKs. */
      targetFilter?: FilterGroup[];
    }
  | { target: null; reason: 'array' | 'polymorphic' | 'json-path' | 'none' };

export type LookupEnumTargetInput = {
  dataType: string | undefined;
  /** From the merged FK map — a real FK constraint on the column, if any. Real FK always wins. */
  realFk: LookupEnumRealFkInput | undefined;
  /** All loose-relationship declarations on the source column triple. */
  looseRels: readonly LooseRelationship[];
};

/**
 * Human-readable name for a lookup target — `schema.table`, or just `table`
 * on schemaless engines (Kusto reports `''`). Shared by every surface that
 * names the referenced table (declare dialog, details dialog, filter picker
 * provenance line).
 */
export function qualifyLookupTarget(schema: string, table: string): string {
  return schema == null || schema === '' ? table : `${schema}.${table}`;
}

/**
 * A filter expression is a no-op when there are no groups, or every group
 * carries zero conditions. Matches the runtime semantics: `filterEvaluator`
 * treats a condition-less group as matching every row and `filterWhereBuilder`
 * drops it before emitting SQL.
 */
function isEmptyFilter(groups: FilterGroup[] | undefined): boolean {
  if (!groups || groups.length === 0) return true;
  return groups.every((g) => (g.conditions?.length ?? 0) === 0);
}

/** `undefined` and `''` are the same "no JSON path" bucket (mirrors `resolveActiveLooseRel`). */
function hasSourcePath(rel: LooseRelationship): boolean {
  return (rel.sourcePath ?? '') !== '';
}

export function resolveLookupEnumTarget(
  input: LookupEnumTargetInput,
): LookupEnumTargetResolution {
  // 1. Array columns can never be a lookup enum — a cell holds many keys, not
  //    one. Checked before the FK rules so an array FK column reports 'array'.
  if (input.dataType?.endsWith('[]')) return { target: null, reason: 'array' };

  // 2. A real FK constraint wins outright — no relationshipId (there's no
  //    loose declaration behind it) and no target filter.
  if (input.realFk) {
    return {
      target: {
        toSchema: input.realFk.referencedSchema ?? '',
        toTable: input.realFk.referencedTable,
        toColumn: input.realFk.referencedColumn,
      },
    };
  }

  // 3. Loose relationships. Path-scoped declarations address JSON sub-keys, not
  //    the column itself, so they never make the column polymorphic — they are
  //    only decisive when there's nothing else (the same exemption the
  //    source-filter overlap detector applies at save time).
  const pathRels = input.looseRels.filter(hasSourcePath);
  const columnRels = input.looseRels.filter((rel) => !hasSourcePath(rel));

  if (columnRels.length === 0) {
    if (pathRels.length > 0) return { target: null, reason: 'json-path' };
    return { target: null, reason: 'none' };
  }

  // Exactly one column-level declaration, and it fires on every row → eligible.
  // Two of them, or one gated by a source filter (polymorphic dispatch), means
  // the target depends on the row — nothing a single enum declaration can hold.
  const only = columnRels.length === 1 ? columnRels[0] : undefined;
  if (!only || !isEmptyFilter(only.sourceFilter)) {
    return { target: null, reason: 'polymorphic' };
  }

  const target: EnumLookupTarget = {
    toSchema: only.toSchema,
    toTable: only.toTable,
    toColumn: only.toColumn,
    relationshipId: only.id,
  };
  return isEmptyFilter(only.targetFilter)
    ? { target }
    : { target, targetFilter: only.targetFilter };
}
